import React, {useEffect, useRef, useState} from 'react';
import {CheckCircle2, Globe, MapPin, MessageCircle, Phone} from 'lucide-react';
import {t} from 'i18next';

const categoryColors = {
	"SHOPPING_RETAIL": "#E8722A", "FOOD_BEVERAGES": "#D97706",
	"BEAUTY_HEALTH": "#DB2777", "PROFESSIONAL_SERVICES": "#4F46E5",
	"EDUCATION_TRAINING": "#0891B2", "EVENTS_LEISURE": "#7C3AED",
	"TECHNOLOGY_IT": "#0284C7", "CONSTRUCTION_HOME": "#65A30D", "OTHER": "#6B7280",
};

const getCategoryLabel = (key) => t(`YELLOW_PAGE.CATEGORIES.${key}`, {defaultValue: key});

const entityTypeLabel = {
	product: 'Products',
	service: 'Services',
	hybrid: 'Products & Services',
};

// ─── Animation helpers ────────────────────────────────────────────────────────
// These mirror the helpers in the onboarding form.
// When updatedFields is not passed (public listing usage), both return ''.
const makeAnimHelpers = (updatedFields = []) => ({
	isUpdated: (f) => updatedFields.includes(f) ? 'pv-updated' : '',
	isHl: (f) => updatedFields.includes(f) ? 'pv-hl' : '',
});

const formatFamilyTreeName = (familyTreeName) => {
	if (!familyTreeName) return '';
	return familyTreeName
		.replace(/-\d+$/, '')        // remove trailing "-1", "-2" etc.
};

// ─── Component ────────────────────────────────────────────────────────────────
const YellowPageListingCard = ({
	                               entityType, entityName, tagline, categories = [], logo,
	                               isVerified,
	                               isPhysical, location, serviceArea,
	                               phone, callingPhone, sameNumberForCalling, website,
	                               primaryCta = 'whatsapp',
	                               ownerName, nativePlace,
	                               ownerImageUrl, ownerImageUrlBg,
	                               onViewProfile,
	                               updatedFields,
	                               isPreview = false,
                               }) => {
	const {isUpdated, isHl} = makeAnimHelpers(updatedFields);
	const primaryColor = categoryColors[categories[0]] || '#D26A53';
	const FALLBACK_IMAGE = '/other_imgs/logo-joglekar kul-72x72.png';

	// ── Derive the primary CTA ─────────────────────────────────────────────
	const cleanCode = phone?.dialingCode?.replace('+', '') || '91';
	const waLink = `https://wa.me/${cleanCode}${phone?.number}`;
	const callNumber = sameNumberForCalling ? phone : callingPhone;
	const telLink = `tel:${callNumber?.dialingCode}${callNumber?.number}`;
	const webLink = website?.startsWith('http') ? website : `https://${website}`;

	const [ctaTooltip, setCtaTooltip] = useState(false);
	const [profileTooltip, setProfileTooltip] = useState(false);
	const ctaTooltipRef = useRef(null);
	const profileTooltipRef = useRef(null);

	const showTooltipFor = (setter, ref) => {
		setter(true);
		if (ref.current) clearTimeout(ref.current);
		ref.current = setTimeout(() => setter(false), 3000);
	};

	// Don't forget cleanup:
	useEffect(() => () => {
		if (ctaTooltipRef.current) clearTimeout(ctaTooltipRef.current);
		if (profileTooltipRef.current) clearTimeout(profileTooltipRef.current);
	}, []);

	const ctaConfig = {
		whatsapp: {
			icon: MessageCircle, label: 'WhatsApp', href: waLink,
			style: {background: '#DCFCE7', color: '#15803D', border: '1px solid #BBF7D0'},
			available: !!phone?.number,
		},
		call: {
			icon: Phone, label: 'Call', href: telLink,
			style: {background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE'},
			available: !!callNumber?.number,
		},
		website: {
			icon: Globe, label: 'Website', href: webLink,
			style: {background: '#F5F3FF', color: '#6D28D9', border: '1px solid #DDD6FE'},
			available: !!website,
		},
	};

	// Fall back through priority if the chosen CTA isn't available yet
	const resolvedCta =
		ctaConfig[primaryCta]?.available ? ctaConfig[primaryCta] :
			ctaConfig['whatsapp']?.available ? ctaConfig['whatsapp'] :
				ctaConfig['call']?.available ? ctaConfig['call'] :
					ctaConfig['website']?.available ? ctaConfig['website'] :
						null;

	const servesBadgeStyle = isPhysical
		? {background: '#FFF7F5', color: '#B54A33', border: '1px solid #F5CEC7'}
		: {background: '#F0FDF4', color: '#15803D', border: '1px solid #BBF7D0'};

	return (
		<div className="w-full rounded-2xl overflow-hidden bg-white transition-all duration-200 hover:shadow-lg"
		     style={{
			     boxShadow: '0 2px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
			     border: '1px solid #F0F0F0',
			     cursor: isPreview ? 'default' : 'pointer',
		     }}>

			{/* ── Coloured top border — animates on category change ─── */}
			<div className={`h-1 w-full transition-all duration-500 ${isUpdated('categories')}`}
			     style={{background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}66)`}}/>

			{/* ── Main content ─────────────────────────────────────── */}
			<div className="p-4 flex gap-3.5">

				{/* Logo + verified badge */}
				<div className="shrink-0 relative pb-3">
					<div
						className={`w-[4.5rem] h-[4.5rem] rounded-xl overflow-hidden flex items-center justify-center transition-all duration-300 ${isUpdated('logo')}`}
						style={{background: '#F8F8F8', border: '1.5px solid #EFEFEF'}}>
						<img src={logo || FALLBACK_IMAGE} alt="Logo"
						     className={`w-full h-full ${logo ? 'object-contain' : 'object-contain p-2 opacity-70'}`}/>
					</div>
					<div className="absolute bottom-1 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap">
						{isVerified ? (
							<div
								className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded-full font-custom-bold text-[0.45rem] uppercase tracking-wider ${isUpdated('otpVerified')}`}
								style={{
									background: '#fff',
									border: '1px solid #D1FAE5',
									color: '#065F46',
									boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
								}}>
								<CheckCircle2 className="w-2 h-2 text-emerald-500"/> Verified
							</div>
						) : (
							<div
								className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full font-custom-bold text-[0.45rem] uppercase tracking-wider"
								style={{
									background: '#fff',
									border: '1px solid #FDE68A',
									color: '#92400E',
									boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
								}}>
								<span className="w-1.5 h-1.5 rounded-full bg-amber-400"/> Pending
							</div>
						)}
					</div>
				</div>

				{/* Name, chips, tagline */}
				<div className="flex flex-col flex-1 min-w-0 pt-0.5">
					<div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
						{entityType && (
							<span
								className={`font-custom-bold text-[0.5rem] uppercase tracking-wider px-2 py-0.5 rounded-md transition-all duration-300 ${isUpdated('entityType')}`}
								style={{
									background: `${primaryColor}14`,
									color: primaryColor,
									border: `1px solid ${primaryColor}30`
								}}>
								{entityTypeLabel[entityType] || entityType}
							</span>
						)}
						{categories[0] && (
							<span
								className={`font-custom-semi-bold text-[0.5rem] px-2 py-0.5 rounded-md truncate max-w-[7.5rem] transition-all duration-300 ${isUpdated('categories')}`}
								style={{
									background: `${primaryColor}14`,
									color: primaryColor,
									border: `1px solid ${primaryColor}30`
								}}>
								{getCategoryLabel(categories[0])}
							</span>
						)}
						{categories.length > 1 && (
							<span
								className="font-custom-bold text-[0.5rem] px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-400 border border-gray-200">
								+{categories.length - 1}
							</span>
						)}
					</div>

					<h4 className={`font-custom-extra-bold text-[1rem] leading-tight truncate tracking-tight transition-all duration-200 ${isHl('entityName')}`}
					    style={{color: entityName ? '#111827' : '#D1D5DB'}}
					    title={entityName}>
						{entityName
							? entityName
							: <em className="font-custom-semi-bold text-sm not-italic">Your Business Name</em>}
					</h4>

					<p
						className={`font-custom-regular text-[0.68rem] mt-1 line-clamp-2 leading-snug transition-all duration-200 ${isHl('tagline')}`}
						style={{color: tagline ? '#6B7280' : '#D1D5DB'}}
					>
						{tagline || <em className="not-italic">Your short, punchy tagline...</em>}
					</p>
				</div>
			</div>

			{/* ── Serves At + Listed By ─────────────────────────── */}
			<div
				className={`px-4 pb-3 flex items-start justify-between gap-3 transition-all duration-300 ${isUpdated('isPhysical') || isUpdated('location') || isUpdated('serviceArea') ? 'pv-updated' : ''}`}
				style={{borderTop: '1px solid #F5F5F5', paddingTop: '10px'}}>

				{/* Listed By */}
				<div className="flex items-center gap-2 shrink-0">
					{/* Avatar */}
					<div
						className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center shrink-0 font-custom-extra-bold text-sm"
						style={{
							background: (ownerImageUrl?.startsWith('http') || ownerImageUrl?.startsWith('/')) ? 'transparent' : (ownerImageUrlBg || '#FDF0ED'),
							border: '2px solid #F5CEC7',
							boxShadow: '0 1px 4px rgba(210,106,83,0.15)',
							color: '#D26A53',
						}}>
						{(ownerImageUrl?.startsWith('http') || ownerImageUrl?.startsWith('/'))
							? <img src={ownerImageUrl} alt={ownerName} className="w-full h-full object-cover"/>
							: <span>{ownerImageUrl || ownerName?.charAt(0) || '?'}</span>
						}
					</div>

					{/* Label + name + native place */}
					<div className="flex flex-col min-w-0">
						<p className="font-custom-bold text-[0.48rem] uppercase tracking-widest text-gray-400 leading-none mb-0.5">
							Listed By
						</p>
						<p className="font-custom-extra-bold text-[0.72rem] text-gray-900 truncate leading-tight"
						   title={ownerName}>
							{ownerName}
						</p>
						<div className="flex items-center gap-1 mt-0.5">
							<MapPin className="w-2.5 h-2.5 shrink-0" style={{color: '#D26A53'}}/>
							<p className="font-custom-semi-bold text-[0.62rem] text-gray-500 truncate">
								मुळ गाव: <span className="font-custom-bold text-gray-800"
								               title={formatFamilyTreeName(nativePlace)}>
						                    {formatFamilyTreeName(nativePlace)}
						                </span>
							</p>
						</div>
					</div>
				</div>

				{/* Serves At */}
				<div className="flex flex-col gap-0.5 min-w-0">
					<p className="font-custom-bold text-[0.48rem] text-right uppercase tracking-widest"
					   style={{color: isPhysical ? '#B54A33' : '#15803D'}}>
						{isPhysical ? 'Serves At' : 'Serves'}
					</p>
					<div className="flex items-center gap-1 font-custom-bold text-[0.62rem] px-2 py-1 rounded-lg"
					     style={servesBadgeStyle}>
						{isPhysical
							? <><MapPin className="w-2.5 h-2.5 shrink-0"/><span className="truncate max-w-[5rem]"
							                                                    title={location}>{location || '——'}</span></>
							: <><Globe className="w-2.5 h-2.5 shrink-0"/><span className="truncate max-w-[5rem]"
							                                                   title={serviceArea}>{serviceArea || 'Online'}</span></>
						}
					</div>
				</div>
			</div>

			{/* ── Action row ── */}
			<div className="px-4 pb-4 flex items-center gap-2"
			     style={{borderTop: '1px solid #F5F5F5', paddingTop: '10px'}}>

				{/* Primary CTA with tooltip */}
				<div className="relative flex-1">
					{resolvedCta ? (
						<a href={isPreview ? undefined : resolvedCta.href}
						   onClick={isPreview ? e => {
							   e.preventDefault();
							   showTooltipFor(setCtaTooltip, ctaTooltipRef);
						   } : undefined}
						   target={isPreview ? undefined : '_blank'}
						   rel="noopener noreferrer"
						   className={`flex items-center gap-1.5 px-4 py-2 rounded-xl font-custom-bold text-[0.72rem] transition-all duration-150 hover:opacity-90 active:scale-95 w-full justify-center ${isUpdated('primaryCta') ? 'pv-updated' : ''}`}
						   style={resolvedCta.style}>
							<resolvedCta.icon className="w-3.5 h-3.5"/>
							{resolvedCta.label}
						</a>
					) : (
						<div
							className="flex items-center gap-1.5 px-4 py-2 rounded-xl font-custom-bold text-[0.72rem] w-full justify-center"
							style={{background: '#F3F4F6', color: '#D1D5DB', border: '1px solid #E5E7EB'}}>
							<MessageCircle className="w-3.5 h-3.5"/> WhatsApp
						</div>
					)}
					{/* CTA Tooltip */}
					{isPreview && (
						<div
							className={`absolute bottom-[calc(100%+8px)] left-0 w-[11rem] transition-all duration-300 pointer-events-none z-50 ${ctaTooltip ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-1 scale-95'}`}>
							<div
								className="bg-slate-800 text-white text-[0.6rem] font-custom-medium p-2.5 rounded-lg text-center shadow-lg leading-snug">
								This button will let customers contact you via your preferred method.
							</div>
							<div className="absolute -bottom-1 left-6 w-2.5 h-2.5 bg-slate-800 rotate-45"/>
						</div>
					)}
				</div>

				{/* View Profile with tooltip */}
				<div className="relative shrink-0">
					<button type="button"
					        onClick={isPreview ? () => showTooltipFor(setProfileTooltip, profileTooltipRef) : onViewProfile}
					        className="flex items-center gap-1 px-4 py-2 rounded-xl font-custom-bold text-[0.72rem] transition-all duration-150 hover:bg-gray-100"
					        style={{border: '1.5px solid #E5E7EB', color: '#6B7280', background: '#fff'}}>
						View Profile →
					</button>
					{/* Profile Tooltip */}
					{isPreview && (
						<div
							className={`absolute bottom-[calc(100%+8px)] right-0 w-[9rem] transition-all duration-300 pointer-events-none z-50 ${profileTooltip ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-1 scale-95'}`}>
							<div
								className="bg-slate-800 text-white text-[0.6rem] font-custom-medium p-2.5 rounded-lg text-center shadow-lg leading-snug">
								This will open the full public profile of your listing.
							</div>
							<div className="absolute -bottom-1 right-6 w-2.5 h-2.5 bg-slate-800 rotate-45"/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default YellowPageListingCard;